/**
 * Build the minimal profile update from the editable draft.
 *
 * A missing birth date is represented as `null` in the form because the
 * DatePicker is intentionally empty. `updateProfileSchema` quite correctly
 * accepts only an actual ISO date, however, and sending that UI `null` turns a
 * harmless name edit into a validation dead end. More importantly, an
 * unhydrated draft must never clear identity data that arrived after mount.
 */
export interface ProfileEditDraft {
  firstName: string
  lastName: string
  dateOfBirth: string | null
  preferredLocale: string
}

export interface ProfileEditSnapshot {
  firstName?: string | null
  lastName?: string | null
  dateOfBirth?: string | null
  preferredLocale?: string | null
}

export function buildProfileEditInput(
  form: ProfileEditDraft,
  profile: ProfileEditSnapshot | null | undefined,
): Record<string, unknown> {
  const input: Record<string, unknown> = {}

  if (form.firstName !== (profile?.firstName ?? '')) input.firstName = form.firstName
  if (form.lastName !== (profile?.lastName ?? '')) input.lastName = form.lastName

  // Date of birth is identity evidence, not a clearable text field. Only emit
  // a concrete choice from the picker; null means “not loaded / not supplied”,
  // never “erase it”. The server remains authoritative for every ISO value.
  if (form.dateOfBirth !== null && form.dateOfBirth !== profile?.dateOfBirth) {
    input.dateOfBirth = form.dateOfBirth
  }

  if (form.preferredLocale !== (profile?.preferredLocale ?? 'es')) {
    input.preferredLocale = form.preferredLocale
  }

  return input
}
